import React, { useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, Button, Space, Steps, Avatar } from 'antd';
import { 
  UserOutlined, 
  TruckOutlined, 
  ToolOutlined, 
  HomeOutlined, 
  FileTextOutlined,
  CarOutlined,
  EnvironmentOutlined,
  BuildOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ActivityType } from '../../../../types/field.types';
import { User, EmploymentType } from '../../../../types/user.types';
import { Vehicle } from '../../../../types/vehicle.types';
import { useAvailableVehicles, getVehicleOptionLabel } from './useAvailableVehicles';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Step } = Steps;

interface GeneralActivityFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onFinish: (values: any) => void;
  form: any;
  users: User[];
  vehicles: Vehicle[];
  title: string;
}

const GeneralActivityFormModal: React.FC<GeneralActivityFormModalProps> = ({
  visible,
  onCancel,
  onFinish,
  form,
  users,
  vehicles,
  title
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const isFieldWorker = selectedUser?.employeeType === EmploymentType.FIELD;
  
  // Kullanıcının zimmetli araçları (sadece 1. adımdaki bilgi kartı için)
  const assignedVehicles = selectedUser?.assignedVehicles?.filter(
    (vehicle: any) => vehicle.currentAssignment && !vehicle.currentAssignment.returnDate
  ) || [];

  // 2. adımda "userIds" alanı DOM'dan çıktığı için preserve ile izleniyor
  const watchedUserIds = Form.useWatch('userIds', { form, preserve: true });
  const selectedUserIds: number[] = Array.isArray(watchedUserIds)
    ? watchedUserIds
    : watchedUserIds ? [watchedUserIds] : [];
  // Birden fazla çalışana atamada sadece havuz araçları seçilebilir
  const isMultiUser = selectedUserIds.length > 1;

  const { vehicles: availableVehicles, loading: vehiclesLoading, hasDates } = useAvailableVehicles(form, {
    enabled: visible && currentStep === 1,
    userId: selectedUserIds[0],
    onlyPool: isMultiUser
  });

  // Cinsiyete göre varsayılan avatar
  const getDefaultAvatar = (user: User | null) => {
    if (!user) return undefined;
    if (user.profilePicture && user.profilePicture.trim() !== '') return user.profilePicture;
    if (user.gender === 'male') return '/assets/img/man.png';
    if (user.gender === 'female') return '/assets/img/woman.png';
    return undefined;
  };

  const handleUserSelect = (userId: number) => {
    const user = users.find(u => u.id === userId);
    setSelectedUser(user || null);
    form.setFieldValue('userIds', userId);
    
    // Araç alanını temizle
    form.setFieldValue('vehicleId', undefined);
  };

  const handleNext = () => {
    form.validateFields(['userIds']).then(() => {
      setCurrentStep(1);
    });
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handleModalCancel = () => {
    setCurrentStep(0);
    setSelectedUser(null);
    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: any) => {
    // onFinish sadece ekrandaki alanları döndürür; 1. adımdaki personel seçimini ekle
    onFinish({ ...values, userIds: values.userIds ?? form.getFieldValue('userIds') });
    setCurrentStep(0);
    setSelectedUser(null);
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleModalCancel}
      footer={null}
      width={700}
    >
      <Steps current={currentStep} style={{ marginBottom: '24px' }}>
        <Step title="Personel Seç" />
        <Step title="Aktivite Detayları" />
      </Steps>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {currentStep === 0 ? (
          // Step 1: Kullanıcı Seçimi
          <>
            <Form.Item
              name="userIds"
              label="Personel Seçin"
              rules={[{ required: true, message: 'Lütfen bir personel seçin!' }]}
            >
              <Select
                placeholder="Personel seçin"
                showSearch
                size="large"
                onChange={handleUserSelect}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={users.map((user) => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName} ${user.employeeType === EmploymentType.FIELD ? '(Saha)' : '(Ofis)'}`
                }))}
              />
            </Form.Item>

            {selectedUser && (
              <div style={{ 
                padding: '16px', 
                background: selectedUser.employeeType === EmploymentType.FIELD ? '#e6f7ff' : '#f6ffed',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Avatar
                  size={48}
                  src={getDefaultAvatar(selectedUser)}
                  icon={!getDefaultAvatar(selectedUser) ? <UserOutlined /> : undefined}
                  style={{
                    backgroundColor: getDefaultAvatar(selectedUser) 
                      ? '#f0f0f0'
                      : (selectedUser.employeeType === EmploymentType.FIELD ? '#1890ff' : '#52c41a')
                  }}
                >
                  {!getDefaultAvatar(selectedUser) && `${selectedUser.firstName[0]}${selectedUser.lastName[0]}`}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selectedUser.employeeType === EmploymentType.FIELD ? (
                      <><EnvironmentOutlined style={{ color: '#1890ff' }} /> Saha Çalışanı</>
                    ) : (
                      <><BuildOutlined style={{ color: '#52c41a' }} /> Ofis Çalışanı</>
                    )}
                  </div>
                  <div style={{ fontWeight: '500' }}>{selectedUser.firstName} {selectedUser.lastName}</div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{selectedUser.email}</div>
                  {selectedUser.employeeType === EmploymentType.FIELD && assignedVehicles.length > 0 && (
                    <div style={{ marginTop: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CarOutlined /> Zimmetli Araç Sayısı: {assignedVehicles.length}
                    </div>
                  )}
                </div>
              </div>
            )}

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleModalCancel}>İptal</Button>
                <Button type="primary" onClick={handleNext} disabled={!selectedUser}>
                  İleri
                </Button>
              </Space>
            </Form.Item>
          </>
        ) : (
          // Step 2: Aktivite Detayları
          <>
            <Form.Item
              name="title"
              label="Aktivite Başlığı"
              rules={[{ required: true, message: 'Lütfen aktivite başlığı girin!' }]}
            >
              <Input placeholder="Örn: Müşteri ziyareti" />
            </Form.Item>

            <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Aktivite Tipi"
                rules={[{ required: true, message: 'Lütfen aktivite tipi seçin!' }]}
              >
                <Select placeholder="Tip seçin">
                  <Option value={ActivityType.VISIT}>
                    <Space><UserOutlined /> Ziyaret</Space>
                  </Option>
                  <Option value={ActivityType.DELIVERY}>
                    <Space><TruckOutlined /> Teslimat</Space>
                  </Option>
                  <Option value={ActivityType.MAINTENANCE}>
                    <Space><ToolOutlined /> Bakım</Space>
                  </Option>
                  <Option value={ActivityType.OFFICE}>
                    <Space><HomeOutlined /> Ofis</Space>
                  </Option>
                  <Option value={ActivityType.OTHER}>
                    <Space><FileTextOutlined /> Diğer</Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
              <Col span={12}>
                <Form.Item
                  name="dates"
                  label="Tarih & Saat Aralığı"
                  rules={[{ required: true, message: 'Lütfen tarih aralığı seçin!' }]}
                >
                  <RangePicker 
                    style={{ width: '100%' }} 
                    showTime={{ 
                      format: 'HH:mm',
                      defaultValue: [dayjs().hour(9).minute(0), dayjs().hour(18).minute(0)]
                    }}
                    format="DD.MM.YYYY HH:mm" 
                    placeholder={['Başlangıç tarihi', 'Bitiş tarihi']}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Araç seçimi - seçilen tarih aralığında müsait araçlar */}
            <Form.Item
              name="vehicleId"
              label={<Space size={4}><CarOutlined /> Araç</Space>}
              extra={
                !hasDates
                  ? 'Müsait araçları görmek için önce tarih aralığı seçin.'
                  : isMultiUser
                    ? 'Birden fazla personele atamada sadece havuz araçları seçilebilir.'
                    : undefined
              }
            >
              <Select
                placeholder={hasDates ? 'Araç seçin (opsiyonel)' : 'Önce tarih aralığı seçin'}
                showSearch
                allowClear
                disabled={!hasDates}
                loading={vehiclesLoading}
                notFoundContent={vehiclesLoading ? 'Yükleniyor...' : 'Bu saatlerde müsait araç yok'}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={availableVehicles.map((vehicle) => ({
                  value: vehicle.id,
                  label: getVehicleOptionLabel(vehicle)
                }))}
              />
            </Form.Item>

            <Form.Item name="description" label="Açıklama">
              <TextArea rows={4} placeholder="Aktivite detaylarını yazın..." />
            </Form.Item>

            {isFieldWorker && (
              <Form.Item name="isBillable" valuePropName="checked" initialValue={false}>
                <Space>
                  <input type="checkbox" />
                  <span>Faturalandırılabilir</span>
                </Space>
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleBack}>Geri</Button>
                <Button onClick={handleModalCancel}>İptal</Button>
                <Button type="primary" htmlType="submit">
                  Oluştur
                </Button>
              </Space>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default GeneralActivityFormModal;

